/**
 * SubscriptionModal Component
 *
 * Full subscription management interface.
 * Shows current plan, usage, billing history, and allows upgrade/cancel.
 */

import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Check, Star } from 'lucide-react';
import { BaseModal } from '../modals/BaseModal';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { stripeService } from '../../services';
import { cn } from '../../lib/utils';

interface SubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void; // Opens JobTailoringPurchaseModal
}

interface SubscriptionData {
  job_credits: number;
  has_subscription: boolean;
  subscription_status?: string;
  jobs_used_this_month?: number;
  jobs_limit_this_month?: number;
  documents_uploaded: number;
  documents_limit: number;
  period_end?: string;
  subscription?: {
    cancel_at_period_end?: boolean;
    canceled_at?: string;
  };
}

type ConfirmAction = 'cancel' | 'reactivate' | null;

export const SubscriptionModal: React.FC<SubscriptionModalProps> = ({
  isOpen,
  onClose,
  onUpgrade,
}) => {
  const [data, setData] = useState<SubscriptionData | null>(null);
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [confirmAction, setConfirmAction] = useState<ConfirmAction>(null);

  useEffect(() => {
    if (isOpen) {
      loadData();
    }
  }, [isOpen]);

  const loadData = async () => {
    setLoading(true);
    try {
      const statusData = await stripeService.getStatus();
      setData(statusData);
    } catch (error) {
      console.error('Failed to load subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Runs after the user confirms in the dialog
  const handleConfirmedAction = async () => {
    const action = confirmAction;
    setConfirmAction(null);
    if (!action) return;

    setCanceling(true);
    try {
      if (action === 'reactivate') {
        await stripeService.reactivateSubscription();
        await loadData();
        toast.success('Subscription reactivated', {
          description: 'Billing will continue at the end of your current period.',
        });
      } else {
        await stripeService.cancelSubscription();
        await loadData();
        toast.success('Subscription cancelled', {
          description: 'It will remain active until the end of your billing period.',
        });
      }
    } catch (error: any) {
      toast.error(
        action === 'reactivate'
          ? 'Failed to reactivate subscription'
          : 'Failed to cancel subscription',
        { description: error.message || 'Please try again.' }
      );
    } finally {
      setCanceling(false);
    }
  };

  if (loading || !data) {
    return (
      <BaseModal isOpen={isOpen} onClose={onClose} maxWidth="lg" title="Subscription">
        {/* Skeleton in the shape of the real layout */}
        <div className="space-y-6">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="h-48 animate-pulse rounded-2xl bg-muted" />
            <div className="h-48 animate-pulse rounded-2xl bg-muted" />
          </div>
          <div className="h-32 animate-pulse rounded-2xl bg-muted" />
        </div>
      </BaseModal>
    );
  }

  const isCanceledAtPeriodEnd = Boolean(data.subscription?.cancel_at_period_end);

  return (
    <>
      <BaseModal isOpen={isOpen} onClose={onClose} maxWidth="4xl" title="Subscription & Credits">
        <div className="space-y-6">
          {/* Current Plan Status */}
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
            {/* Plan Card */}
            <div className="rounded-2xl border border-border bg-card/80 p-6">
              <h3 className="mb-4 text-lg font-semibold">Current Plan</h3>

              {data.has_subscription ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="text-xl font-bold tracking-tight text-primary">Monthly</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Price</span>
                    <span className="font-semibold text-foreground">$14.99/month</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Status</span>
                    <span
                      className={cn(
                        'inline-flex rounded-full px-3 py-1 text-sm capitalize',
                        isCanceledAtPeriodEnd
                          ? 'bg-warning/15 text-warning'
                          : 'bg-success/15 text-success'
                      )}
                    >
                      {isCanceledAtPeriodEnd ? 'Canceling' : data.subscription_status || 'Active'}
                    </span>
                  </div>
                  {data.period_end && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">
                        {isCanceledAtPeriodEnd ? 'Active until' : 'Next billing'}
                      </span>
                      <span className="text-foreground/85">
                        {new Date(data.period_end).toLocaleDateString()}
                      </span>
                    </div>
                  )}

                  {isCanceledAtPeriodEnd && (
                    <div className="rounded-xl border border-warning/25 bg-warning/10 p-3 text-sm text-warning">
                      Your subscription will end on{' '}
                      {data.period_end
                        ? new Date(data.period_end).toLocaleDateString()
                        : 'the next billing date'}
                      .
                    </div>
                  )}

                  <Button
                    onClick={() =>
                      setConfirmAction(isCanceledAtPeriodEnd ? 'reactivate' : 'cancel')
                    }
                    disabled={canceling}
                    variant={isCanceledAtPeriodEnd ? 'outline' : 'destructive'}
                    className="mt-4 w-full"
                  >
                    {canceling
                      ? 'Processing…'
                      : isCanceledAtPeriodEnd
                        ? 'Reactivate Subscription'
                        : 'Cancel Subscription'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">Plan</span>
                    <span
                      className={cn(
                        'text-xl font-bold tracking-tight',
                        data.job_credits > 0 ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      {data.job_credits > 0 ? 'Starter' : 'Free'}
                    </span>
                  </div>
                  {data.job_credits > 0 ? (
                    <p className="text-sm text-muted-foreground">
                      You have{' '}
                      <span className="font-semibold text-primary">
                        {data.job_credits} free job tailoring credit
                        {data.job_credits !== 1 ? 's' : ''}
                      </span>{' '}
                      to get started!
                    </p>
                  ) : (
                    <p className="text-sm text-foreground/55">
                      Purchase credits to unlock job tailoring features
                    </p>
                  )}
                  <Button onClick={onUpgrade} className="mt-4 w-full">
                    {data.job_credits > 0 ? 'Get More Credits' : 'Purchase Credits'}
                  </Button>
                </div>
              )}
            </div>

            {/* Credits Card */}
            <div className="rounded-2xl border border-border bg-card/80 p-6">
              <h3 className="mb-4 text-lg font-semibold">Job Credits</h3>

              <div className="space-y-4">
                <div className="py-4 text-center">
                  <div className="mb-2 text-5xl font-bold tracking-tight text-primary tabular-nums">
                    {data.job_credits}
                  </div>
                  <div className="text-sm text-muted-foreground">Available Credits</div>
                </div>

                {!data.has_subscription && (
                  <Button onClick={onUpgrade} variant="secondary" className="w-full">
                    Purchase More Credits
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Usage This Period */}
          {data.has_subscription && (
            <div className="rounded-2xl border border-border bg-card/80 p-6">
              <h3 className="mb-4 text-lg font-semibold">This Billing Period</h3>

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">Jobs Created</span>
                  <span className="text-2xl font-bold tracking-tight text-foreground tabular-nums">
                    {data.jobs_used_this_month || 0}/{data.jobs_limit_this_month || 8}
                  </span>
                </div>
                <div
                  role="progressbar"
                  aria-valuenow={data.jobs_used_this_month || 0}
                  aria-valuemin={0}
                  aria-valuemax={data.jobs_limit_this_month || 8}
                  aria-label="Jobs used this billing period"
                  className="h-2 w-full overflow-hidden rounded-full bg-muted"
                >
                  <div
                    className="h-full rounded-full bg-primary transition-all duration-300"
                    style={{
                      width: `${((data.jobs_used_this_month || 0) / (data.jobs_limit_this_month || 8)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Features Comparison */}
          <div className="rounded-2xl border border-border bg-card/80 p-6">
            <h3 className="mb-4 text-lg font-semibold">
              {data.has_subscription ? 'Your Benefits' : 'Upgrade Benefits'}
            </h3>

            <p className="mb-4 text-sm text-muted-foreground">8 job tailorings per month:</p>

            <div className="mb-4 space-y-3 text-sm">
              <div className="flex items-start text-foreground/70">
                <Check className="mt-0.5 mr-2 size-4 shrink-0 text-success" />
                <span>Everything in Job Tailoring Credit</span>
              </div>
            </div>

            <p className="mb-2 text-sm font-medium text-primary">Subscriber-only perk:</p>
            <div className="mb-4 space-y-3 text-sm">
              <div className="flex items-start text-foreground/70">
                <Star className="mt-0.5 mr-2 size-4 shrink-0 text-primary" />
                <span>Resume regeneration for ANY job, even jobs purchased with credits</span>
              </div>
            </div>

            <div className="border-t border-border pt-4 text-xs text-foreground/55">
              *Up to 25 messages sent between all AI agents for each job tailoring
            </div>
          </div>
        </div>
      </BaseModal>

      {/* Cancel / Reactivate confirmation */}
      <Dialog
        open={confirmAction !== null}
        onOpenChange={(open) => {
          if (!open) setConfirmAction(null);
        }}
      >
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>
              {confirmAction === 'reactivate'
                ? 'Reactivate your subscription?'
                : 'Cancel your subscription?'}
            </DialogTitle>
            <DialogDescription>
              {confirmAction === 'reactivate'
                ? 'Billing will continue at the end of your current period.'
                : 'Your subscription will remain active until the end of your billing period.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>
              Go Back
            </Button>
            {confirmAction === 'reactivate' ? (
              <Button onClick={handleConfirmedAction}>Reactivate Subscription</Button>
            ) : (
              <Button variant="destructive" onClick={handleConfirmedAction}>
                Cancel Subscription
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
